import React, { useCallback, useEffect, useRef, useState } from "react";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import {
  executeMutation,
  executeQuery,
} from "../../../graphql/client";
import FlightBoard, {
  regionName,
} from "../../other/FlightBoard/FlightBoard";
import "./FlyScraperTab.css";

// Scraper status + run — graphql flyStatus/flyRun (#134 phase 1;
// was the pendingJoinRequests REST ?type=flyStatus / flyRun).
const FLY_STATUS_QUERY = `
  query FlyStatus {
    flyStatus {
      bucket
      functionName
      flights {
        count
        lastModified
        items {
          origin
          destination
          price
          startDate
          endDate
          rawDates
          stops
          regionDestination
          updatedAt
        }
      }
      flightsError
      lastRunJson
    }
  }
`;

const FLY_RUN_MUTATION = `
  mutation FlyRun {
    flyRun {
      started
      error
      hint
    }
  }
`;

// Recent price clicks by users on the Flights board (member Apps tab
// or this admin tab) — who clicked what deal. With userSub: one
// user's full click history (the per-viewer drill-down).
const FLIGHT_CLICKS = gql`
  query FlightClicks($limit: Int, $userSub: String) {
    flightClicks(limit: $limit, userSub: $userSub) {
      id
      clickedAt
      email
      name
      origin
      destination
      price
      startDate
      endDate
      regionDestination
      stops
    }
  }
`;

// Recent Flights-page loads — one row per user per hour-apart visit
// (recordFlightView dedupes closer loads server-side).
const FLIGHT_VIEWS = gql`
  query FlightViews($limit: Int) {
    flightViews(limit: $limit) {
      id
      viewedAt
      userSub
      email
      name
    }
  }
`;

// Admin Fly Scraper App: run and monitor the flight-price scraper
// (fly/scrape) without touching the AWS console. The flight display
// itself (time filters, favorite regions, Best Price by Region,
// per-region tables) is the shared FlightBoard component — the same
// board members see in the dashboard's Apps tab. This admin tab adds
// what members don't get: the Run-scrape button with fresh-data
// polling, status cards, and the last run's performance log.

const POLL_MS = 30000;
const MAX_POLLS = 40; // ~20 minutes

const fmtWhen = (raw) => {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleString();
  } catch {
    return String(raw);
  }
};

// Click-log table, shared by the recent-clicks section (with a User
// column) and the per-viewer drill-down (without one).
const ClicksTable = ({ clicks, showUser }) => (
  <div className="fly-tab__table-wrap">
    <table className="fly-tab__table">
      <thead>
        <tr>
          <th>When</th>
          {showUser && <th>User</th>}
          <th>Flight</th>
          <th>Price</th>
          <th>Dates</th>
          <th>Region</th>
          <th>Stops</th>
        </tr>
      </thead>
      <tbody>
        {clicks.map((c) => (
          <tr key={c.id}>
            <td className="fly-tab__muted">
              {c.clickedAt
                ? new Date(c.clickedAt).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })
                : "—"}
            </td>
            {showUser && (
              <td>
                {c.name || "—"}
                {c.email && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#8a93a3",
                      wordBreak: "break-all",
                    }}
                  >
                    {c.email}
                  </div>
                )}
              </td>
            )}
            <td>
              {c.origin || "?"} → {c.destination || "?"}
            </td>
            <td className="fly-tab__price">
              {c.price != null ? `$${c.price}` : "—"}
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
              {c.startDate
                ? `${c.startDate}${c.endDate ? ` – ${c.endDate}` : ""}`
                : "—"}
            </td>
            <td className="fly-tab__muted">
              {regionName(c.regionDestination)}
            </td>
            <td className="fly-tab__muted">
              {c.stops != null ? c.stops : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const FlyScraperTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [runState, setRunState] = useState("idle"); // idle | starting | running | fresh
  const [runError, setRunError] = useState("");
  const [startedAt, setStartedAt] = useState(null);
  const baselineRef = useRef(null);
  const pollCountRef = useRef(0);
  const pollTimerRef = useRef(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await executeQuery(FLY_STATUS_QUERY);
      const raw = data?.flyStatus || {};
      // lastRun arrives as a JSON string (shape varies run to run);
      // parse here so the k/v render below keeps working unchanged.
      let lastRun = null;
      if (raw.lastRunJson) {
        try {
          lastRun = JSON.parse(raw.lastRunJson);
        } catch {
          lastRun = { raw: raw.lastRunJson };
        }
      }
      const mapped = { ...raw, lastRun };
      setStatus(mapped);
      return mapped;
    } catch (err) {
      setError(err.message || "Couldn't load scraper status.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [loadStatus]);

  const pollForFreshData = useCallback(async () => {
    pollCountRef.current += 1;
    const data = await loadStatus();
    const modified = data?.flights?.lastModified || null;
    if (modified && modified !== baselineRef.current) {
      setRunState("fresh");
      return;
    }
    if (pollCountRef.current >= MAX_POLLS) {
      setRunState("idle");
      setRunError(
        "No new data after ~20 minutes — check the fly-scraper CloudWatch logs."
      );
      return;
    }
    pollTimerRef.current = setTimeout(pollForFreshData, POLL_MS);
  }, [loadStatus]);

  const runScrape = async () => {
    setRunError("");
    setRunState("starting");
    baselineRef.current = status?.flights?.lastModified || null;
    pollCountRef.current = 0;
    try {
      const data = await executeMutation(FLY_RUN_MUTATION);
      const result = data?.flyRun || {};
      if (!result.started) {
        throw new Error(
          [result.error, result.hint].filter(Boolean).join(" — ") ||
            "Couldn't start the scrape."
        );
      }
      setStartedAt(Date.now());
      setRunState("running");
      pollTimerRef.current = setTimeout(pollForFreshData, POLL_MS);
    } catch (err) {
      setRunState("idle");
      setRunError(err.message || "Couldn't start the scrape.");
    }
  };

  const {
    data: clicksData,
    loading: clicksLoading,
    error: clicksError,
    refetch: refetchClicks,
  } = useQuery(FLIGHT_CLICKS, {
    variables: { limit: 100 },
    fetchPolicy: "network-only",
  });
  const clicks = clicksData?.flightClicks || [];

  const {
    data: viewsData,
    loading: viewsLoading,
    error: viewsError,
    refetch: refetchViews,
  } = useQuery(FLIGHT_VIEWS, {
    variables: { limit: 100 },
    fetchPolicy: "network-only",
  });
  const views = viewsData?.flightViews || [];

  // Per-viewer drill-down: click a page-view row to see every flight
  // that user has ever clicked.
  const [selectedViewer, setSelectedViewer] = useState(null);
  const [
    loadUserClicks,
    {
      data: userClicksData,
      loading: userClicksLoading,
      error: userClicksError,
    },
  ] = useLazyQuery(FLIGHT_CLICKS, { fetchPolicy: "network-only" });
  const userClicks = userClicksData?.flightClicks || [];

  const openViewer = (v) => {
    setSelectedViewer({
      userSub: v.userSub,
      name: v.name,
      email: v.email,
    });
    loadUserClicks({ variables: { limit: 500, userSub: v.userSub } });
  };

  const flights = status?.flights;
  const minsSinceStart = startedAt
    ? Math.round((Date.now() - startedAt) / 60000)
    : 0;

  return (
    <div className="fly-tab">
      <div className="fly-tab__head">
        <div>
          <h2 className="fly-tab__title">✈️ Fly Scraper</h2>
          <p className="fly-tab__sub">
            Flight prices from fly/scrape (cloud runs at ~6am/6pm ET, or
            local runs published with fly ship). Members see this same
            board in their Apps tab — minus the controls below.
          </p>
        </div>
        <div className="fly-tab__actions">
          <button
            type="button"
            className="fly-tab__btn"
            onClick={() => loadStatus()}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            type="button"
            className="fly-tab__btn fly-tab__btn--primary"
            onClick={runScrape}
            disabled={runState === "starting" || runState === "running"}
          >
            {runState === "starting"
              ? "Starting…"
              : runState === "running"
              ? `Running… (~${minsSinceStart} min)`
              : "Run scrape now"}
          </button>
        </div>
      </div>

      {error && <div className="fly-tab__error">{error}</div>}
      {runError && <div className="fly-tab__error">{runError}</div>}
      {runState === "running" && (
        <div className="fly-tab__info">
          Scrape started — watching for new data every 30s. Leave this
          tab open, or check the ✈️ Slack summary / CloudWatch logs.
        </div>
      )}
      {runState === "fresh" && (
        <div className="fly-tab__success">
          🎉 Fresh data landed! Tables below show the new scrape.
        </div>
      )}

      <div className="fly-tab__cards">
        <div className="fly-tab__card">
          <div className="fly-tab__card-label">Flights in data</div>
          <div className="fly-tab__card-value">
            {flights ? flights.count : "—"}
          </div>
        </div>
        <div className="fly-tab__card">
          <div className="fly-tab__card-label">Data last updated</div>
          <div className="fly-tab__card-value fly-tab__card-value--small">
            {fmtWhen(flights?.lastModified)}
          </div>
        </div>
        <div className="fly-tab__card">
          <div className="fly-tab__card-label">Last run log</div>
          <div className="fly-tab__card-value fly-tab__card-value--small">
            {status?.lastRun ? fmtWhen(status.lastRun.timestamp) : "—"}
          </div>
        </div>
      </div>

      {status?.flightsError && (
        <div className="fly-tab__warn">
          No flight data readable yet ({status.flightsError}).
          {String(status.flightsError).includes("AccessDenied")
            ? " The graphql Lambda role needs the fly-data S3 policy — redeploy the graphql stack (its SAM template grants it)."
            : " Run a scrape (here or locally with fly ship) to populate it."}
        </div>
      )}

      <FlightBoard items={flights?.items || []} />

      <div className="fly-tab__head" style={{ marginTop: 18 }}>
        <h3 className="fly-tab__section" style={{ margin: 0 }}>
          👀 Recent page views{views.length ? ` (${views.length})` : ""}
        </h3>
        <button
          type="button"
          className="fly-tab__btn"
          onClick={() => refetchViews()}
          disabled={viewsLoading}
        >
          {viewsLoading ? "Loading…" : "Refresh views"}
        </button>
      </div>
      {viewsError && (
        <div className="fly-tab__error">
          Couldn't load page views: {viewsError.message}
        </div>
      )}
      {!viewsError && views.length === 0 ? (
        <p style={{ color: "#8a93a3", fontSize: 13 }}>
          No page views recorded yet — a row appears each time a user
          opens the Flights app (loads under an hour apart count as
          one visit).
        </p>
      ) : viewsError ? null : (
        <div className="fly-tab__table-wrap">
          <table className="fly-tab__table">
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {views.map((v) => (
                <tr
                  key={v.id}
                  onClick={() => openViewer(v)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="fly-tab__muted">
                    {v.viewedAt
                      ? new Date(v.viewedAt).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                  <td>
                    {v.name || "—"}
                    {v.email && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#8a93a3",
                          wordBreak: "break-all",
                        }}
                      >
                        {v.email}
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="fly-tab__btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openViewer(v);
                      }}
                    >
                      Their clicks
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedViewer && (
        <div style={{ marginTop: 14 }}>
          <div className="fly-tab__head">
            <h3 className="fly-tab__section" style={{ margin: 0 }}>
              👆 All clicks by {selectedViewer.name || "this user"}
              {selectedViewer.email ? ` (${selectedViewer.email})` : ""}
              {!userClicksLoading && !userClicksError
                ? ` — ${userClicks.length} total`
                : ""}
            </h3>
            <button
              type="button"
              className="fly-tab__btn"
              onClick={() => setSelectedViewer(null)}
            >
              Close
            </button>
          </div>
          {userClicksLoading && (
            <p style={{ color: "#8a93a3", fontSize: 13 }}>
              Loading their click history…
            </p>
          )}
          {userClicksError && (
            <div className="fly-tab__error">
              Couldn't load their clicks: {userClicksError.message}
            </div>
          )}
          {!userClicksLoading &&
            !userClicksError &&
            (userClicks.length === 0 ? (
              <p style={{ color: "#8a93a3", fontSize: 13 }}>
                No clicks recorded for this user yet.
              </p>
            ) : (
              <ClicksTable clicks={userClicks} showUser={false} />
            ))}
        </div>
      )}

      <div className="fly-tab__head" style={{ marginTop: 18 }}>
        <h3 className="fly-tab__section" style={{ margin: 0 }}>
          👆 Recent user clicks{clicks.length ? ` (${clicks.length})` : ""}
        </h3>
        <button
          type="button"
          className="fly-tab__btn"
          onClick={() => refetchClicks()}
          disabled={clicksLoading}
        >
          {clicksLoading ? "Loading…" : "Refresh clicks"}
        </button>
      </div>
      {clicksError && (
        <div className="fly-tab__error">
          Couldn't load clicks: {clicksError.message}
        </div>
      )}
      {!clicksError && clicks.length === 0 ? (
        <p style={{ color: "#8a93a3", fontSize: 13 }}>
          No clicks recorded yet — they'll appear here as users tap
          prices on the Flights board.
        </p>
      ) : clicksError ? null : (
        <ClicksTable clicks={clicks} showUser />
      )}

      {status?.lastRun && (
        <>
          <h3 className="fly-tab__section">Last run details</h3>
          <div className="fly-tab__kv">
            {Object.entries(status.lastRun).map(([k, v]) => (
              <div key={k} className="fly-tab__kv-row">
                <span className="fly-tab__kv-key">{k}</span>
                <span className="fly-tab__kv-val">
                  {typeof v === "object" ? JSON.stringify(v) : String(v)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FlyScraperTab;
